import { memo, useCallback } from "react";

import type { BillingStatus, TicketListAvailable_BillingPageQuery } from "@/lib/gql/__generated__/graphql";

import { Button } from "@/components/ui/button/button";

import { IslandBasicTicket, IslandProTicket } from "./components";

import type {
	BillingSettingDialogStateType,
	BillingSettingDialogType,
	OnUpdatePurchaseBasicBillingTicketType,
	OnUpdatePurchaseProBillingTicketType,
	OnUpdateRecurringPurchaseProBillingTicketType,
} from "../../../model/use-case";

type Props = {
	hotelID: string;

	ticketBasicListAvailable: TicketListAvailable_BillingPageQuery["ticketBasicListAvailable"];
	ticketProListAvailableContinuousBilling: TicketListAvailable_BillingPageQuery["ticketProListAvailableContinuousBilling"];
	ticketProListAvailableSingleBilling: TicketListAvailable_BillingPageQuery["ticketProListAvailableSingleBilling"];

	isUsingPro: boolean;

	billingStatus: BillingStatus;
	onPurchaseBasicBillingTicket: OnUpdatePurchaseBasicBillingTicketType;
	onPurchaseProBillingTicket: OnUpdatePurchaseProBillingTicketType;
	onRecurringPurchaseProBillingTicket: OnUpdateRecurringPurchaseProBillingTicketType;

	openBillingSettingDialog: BillingSettingDialogStateType;
	handleOpenChange: (type: BillingSettingDialogType) => (callBackFunc: NodeJS.Timeout) => (open: boolean) => void;
	activateBillingSettingDialog: (type: BillingSettingDialogType) => void;
	deactivateBillingSettingDialog: (type: BillingSettingDialogType) => void;

	navigateToCancellationPage: () => void;
};

export const SettingsBillingMain = memo(
	({
		hotelID,
		ticketBasicListAvailable,
		ticketProListAvailableContinuousBilling,
		ticketProListAvailableSingleBilling,

		isUsingPro,

		billingStatus,
		onPurchaseBasicBillingTicket,
		onPurchaseProBillingTicket,
		onRecurringPurchaseProBillingTicket,

		openBillingSettingDialog,
		handleOpenChange,
		activateBillingSettingDialog,
		deactivateBillingSettingDialog,

		navigateToCancellationPage,
	}: Props) => {
		const handleClickNavigationButton = useCallback(() => {
			navigateToCancellationPage();
		}, [navigateToCancellationPage]);

		return (
			<>
				<IslandBasicTicket
					hotelID={hotelID}
					ticketBasicListAvailable={ticketBasicListAvailable}
					billingStatus={billingStatus}
					onPurchaseBasicBillingTicket={onPurchaseBasicBillingTicket}
					openBillingSettingDialog={openBillingSettingDialog}
					handleOpenChange={handleOpenChange}
					activateBillingSettingDialog={activateBillingSettingDialog}
					deactivateBillingSettingDialog={deactivateBillingSettingDialog}
				/>

				<IslandProTicket
					ticketProListAvailableContinuousBilling={ticketProListAvailableContinuousBilling}
					ticketProListAvailableSingleBilling={ticketProListAvailableSingleBilling}
					isUsingPro={isUsingPro}
					billingStatus={billingStatus}
					onPurchaseProBillingTicket={onPurchaseProBillingTicket}
					onRecurringPurchaseProBillingTicket={onRecurringPurchaseProBillingTicket}
					openBillingSettingDialog={openBillingSettingDialog}
					handleOpenChange={handleOpenChange}
					activateBillingSettingDialog={activateBillingSettingDialog}
					deactivateBillingSettingDialog={deactivateBillingSettingDialog}
				/>

				<div className="mx-auto w-full p-4 text-right desktop:px-12 desktop:pb-12">
					<Button
						onClick={handleClickNavigationButton}
						type="button"
						variant="link"
						className="text-[17px] text-link-400"
					>
						解約手続きはこちら
					</Button>
				</div>
			</>
		);
	},
);

SettingsBillingMain.displayName = "SettingsBillingMain";
